import i18next from 'i18next';

// Modalità di servizio (classificatore multimodale di primo livello).
// NB: il campo dati è `service_mode`, DISTINTO dall'enum CRM `service_type`
// (che ha semantica medica: hospital_transport, discharge, ...). Non vanno
// confusi né sovrapposti: scrivere valori multimodali su `service_type`
// romperebbe la sync CRM (enum Postgres).
export const SERVICE_MODES = {
  TRANSPORT: 'transport',
  TRANSPORT_OBJECTS: 'transport_objects',
  AMBULANCE: 'ambulance',
  ASSISTANCE: 'assistance',
  TRAIN: 'train',
};

// Label italiane statiche — fallback per i contesti senza i18n inizializzato.
// In UI preferire getServiceModeLabel (i18n).
export const SERVICE_MODES_LABELS = {
  transport: 'Trasporto',
  transport_objects: 'Trasporto oggetti',
  ambulance: 'Ambulanza',
  assistance: 'Assistenza',
  train: 'Treno',
};

// Metadata di presentazione per modalità di servizio.
// `variant` mappa alle famiglie di <AppStatusChip>; `icon` è FontAwesome
// SENZA prefisso `fa-solid fa-`; `labelKey` è la chiave i18n.
export const SERVICE_MODE_META = {
  transport: { variant: 'teal', icon: 'van-shuttle', labelKey: 'service_mode_transport' },
  transport_objects: { variant: 'info', icon: 'box', labelKey: 'service_mode_transport_objects' },
  ambulance: { variant: 'danger', icon: 'ambulance', labelKey: 'service_mode_ambulance' },
  assistance: { variant: 'purple', icon: 'hands-helping', labelKey: 'service_mode_assistance' },
  train: { variant: 'warning', icon: 'train', labelKey: 'service_mode_train' },
};

// Modalità "a punto singolo": hanno UN solo indirizzo (non una tratta
// partenza→arrivo). Le assistenze avvengono presso un singolo luogo.
export const SINGLE_POINT_MODES = [SERVICE_MODES.ASSISTANCE];

export const isSinglePointMode = (mode) => SINGLE_POINT_MODES.includes(mode);

// Nome del campo che porta l'indirizzo del servizio sull'order/leg.
// Una modalità a punto singolo (assistenza) ha UN luogo: il campo è
// `indirizzo`, non `indirizzo_da` — che è la PARTENZA di una tratta e non ha
// senso quando non esiste un arrivo.
export const SINGLE_POINT_ADDRESS_KEY = 'indirizzo';
export const ROUTE_FROM_ADDRESS_KEY = 'indirizzo_da';

export const addressKeyFor = (mode) =>
  isSinglePointMode(mode) ? SINGLE_POINT_ADDRESS_KEY : ROUTE_FROM_ADDRESS_KEY;

/**
 * Indirizzo del servizio letto da un order/leg secondo la sua modalità.
 * Per le modalità a punto singolo c'è un fallback su `indirizzo_da`: gli ordini
 * di assistenza creati prima della separazione dei due campi lo usano ancora e
 * devono continuare a mostrare il luogo.
 */
export const getServiceAddress = (order = {}) => {
  if (isSinglePointMode(getServiceMode(order))) {
    return order[SINGLE_POINT_ADDRESS_KEY] || order[ROUTE_FROM_ADDRESS_KEY] || '';
  }
  return order[ROUTE_FROM_ADDRESS_KEY] || '';
};

// Modalità che trasportano PERSONE: solo qui hanno senso i conteggi passeggeri
// (carrozzina, allettati, accompagnatori) e l'occupazione dei posti del veicolo.
// Un'assistenza avviene presso l'utente, un trasporto oggetti non ha passeggeri,
// un biglietto del treno non occupa un mezzo della flotta.
export const PASSENGER_MODES = [SERVICE_MODES.TRANSPORT, SERVICE_MODES.AMBULANCE];

export const isPassengerMode = (mode) => PASSENGER_MODES.includes(mode);

// Default per gli order legacy privi del campo (retrocompatibilità).
export const DEFAULT_SERVICE_MODE = SERVICE_MODES.TRANSPORT;

// Ricava la modalità di servizio di un order, con fallback a `transport`.
export const getServiceMode = (order) => {
  const m = order?.service_mode;
  return m && SERVICE_MODE_META[m] ? m : DEFAULT_SERVICE_MODE;
};

// Label localizzata della modalità, con fallback alla label italiana statica.
export const getServiceModeLabel = (mode) => {
  const key = SERVICE_MODE_META[mode] ? mode : DEFAULT_SERVICE_MODE;
  return i18next.t(SERVICE_MODE_META[key].labelKey, {
    defaultValue: SERVICE_MODES_LABELS[key],
  });
};

// Opzioni per select/filtri (calcolate on-demand per riflettere la lingua corrente).
export const getServiceModeOptions = () =>
  Object.keys(SERVICE_MODE_META).map((value) => ({
    value,
    label: getServiceModeLabel(value),
  }));
